/**
 * @file agendaChecks.ts
 * @brief Agenda check boxes during the meeting (B49): the recorder's tick list,
 *        kept with the recording clock and sent with the upload.
 *
 * @details
 * The items come from `GET /api/boardroom/agenda-items` (the exact items
 * synthesis checks); offline or on an error the recorder splits the cached
 * agenda notes itself, one item per non-empty line, as the contract allows.
 * Nothing is sent while recording: ticks live in the draft next to the
 * bookmarks and go up once, in `POST /sessions` (`agendaChecks`). After Stop
 * they are frozen; there is no write afterwards.
 *
 * `null` = no check boxes were shown (no agenda, Meeting Notes, or the switch
 * is off): the upload then leaves the field out, as an older app does.
 *
 * No imports: node:test runs it directly (agendaChecks.test.ts); the fetch is
 * in useRecorderAgendaChecks.ts.
 *
 * @references
 * - docs/product/agenda-checks-b49.md (contract)
 * - BACKEND src/features/boardroom/agendaChecks.mjs
 *
 * @author DES
 * @date 02-11-2026
 */

/** One agenda item as the recorder holds it (stored in the draft). */
export type AgendaCheck = {
  noteId: string;
  noteTitle: string;
  index: number;
  text: string;
  /** Sub-point: the point it sits under (shown indented). */
  parentText?: string | null;
  checked: boolean;
  /** Seconds into the recording when ticked (recording clock, like bookmarks). */
  atSecs?: number | null;
};

/** An agenda note's items, from the server or split locally. */
export type AgendaItemsNote = {
  noteId: string;
  noteTitle: string;
  items: { index: number; text: string; parentText?: string | null }[];
};

/** Stable key of one item in the recorder (note + position). */
export function agendaCheckKey(c: Pick<AgendaCheck, "noteId" | "index">): string {
  return `${c.noteId}:${c.index}`;
}

/**
 * The new item list, keeping the ticks of items that are still there (the
 * agenda notes changed mid-recording, or the server list replaced a local
 * split). Matched by note + wording, so a reordered note keeps its ticks.
 */
export function mergeAgendaItems(
  prev: AgendaCheck[] | null,
  notes: AgendaItemsNote[],
): AgendaCheck[] {
  const pool = [...(prev ?? [])];
  const take = (noteId: string, text: string): AgendaCheck | null => {
    const i = pool.findIndex((p) => p.noteId === noteId && p.text.trim() === text.trim());
    if (i < 0) return null;
    return pool.splice(i, 1)[0];
  };
  return notes.flatMap((n) =>
    n.items.map((item) => {
      const was = take(n.noteId, item.text);
      return {
        noteId: n.noteId,
        noteTitle: n.noteTitle,
        index: item.index,
        text: item.text,
        parentText: item.parentText ?? null,
        checked: !!was?.checked,
        atSecs: was?.checked ? (was.atSecs ?? null) : null,
      };
    }),
  );
}

/** Tap = done at `atSecs`; tap again = undo (the time goes with it). */
export function toggleAgendaCheck(
  list: AgendaCheck[],
  key: string,
  atSecs: number,
): AgendaCheck[] {
  return list.map((c) =>
    agendaCheckKey(c) !== key
      ? c
      : c.checked
        ? { ...c, checked: false, atSecs: null }
        : { ...c, checked: true, atSecs: Math.max(0, Math.round(atSecs * 10) / 10) },
  );
}

export function agendaCheckCounts(list: AgendaCheck[] | null): { done: number; total: number } {
  const items = list ?? [];
  return { done: items.filter((c) => c.checked).length, total: items.length };
}

/** The upload's `agendaChecks`: every shown item in order, or undefined to leave it out. */
export function agendaChecksForUpload(list: AgendaCheck[] | null | undefined) {
  if (!Array.isArray(list)) return undefined;
  return list.map((c) => ({
    noteId: c.noteId,
    noteTitle: c.noteTitle,
    index: c.index,
    text: c.text,
    checked: c.checked,
    ...(c.checked && typeof c.atSecs === "number" ? { atSecs: c.atSecs } : {}),
  }));
}

/**
 * Offline / error fallback: one item per non-empty trimmed line of the note's
 * points, in order (the server matches by wording and position, so both
 * work). `levels` marks sub-points, whose parent is the last top-level line.
 */
export function splitAgendaNote(
  noteId: string,
  noteTitle: string,
  points: string[],
  levels: number[] | null,
): AgendaItemsNote {
  let parent: string | null = null;
  return {
    noteId,
    noteTitle: noteTitle.trim() || "Agenda",
    items: points.map((text, index) => {
      const sub = !!levels?.[index];
      if (!sub) parent = text;
      return { index, text, parentText: sub ? parent : null };
    }),
  };
}
